/*
 *  This file newrelic-handler.service.ts is part of HROne Inbox. The intelectual property owned by Uneecops Workplace Solution PVT. LTD.
 *  CopyrightYear: 2025
 *  (C) 2015-2025. All Right reserved. Uneecops Workplace Solution PVT. LTD.
 */

import { inject, Injectable, NgZone } from '@angular/core';
import { BrowserAgent } from '@newrelic/browser-agent/loaders/browser-agent';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class NewRelicHandlerService {

  private agent: BrowserAgent | null = null;

  get isEnabled(): boolean {
    return this.agent !== null;
  }
  private ngZone = inject(NgZone)
  constructor() {
    this.initAgent();
  }

  private initAgent(): void {
    if (!environment.newRelic?.enabled) {
      return;
    }
    const { accountID, trustKey, agentID, licenseKey, applicationID } = environment.newRelic;
    const nativeWarn = console.warn.bind(console);
    const nativeError = console.error.bind(console);
    try {
      this.ngZone.runOutsideAngular(()=>{
      this.agent = new BrowserAgent({
        init: {
          distributed_tracing: { enabled: true },
          privacy: { cookies_enabled: true },
          ajax: { deny_list: [], enabled: true },
          session_trace: { enabled: true },
          session_replay: { enabled: true },
          jserrors: { enabled: true, autoStart: true },
          logging: { enabled: true, autoStart: true },
          metrics: { enabled: true },
          page_action: { enabled: true }
        },
        info: {
          beacon: 'bam.nr-data.net',
          errorBeacon: 'bam.nr-data.net',
          licenseKey,
          applicationID,
          sa: 1
        },
        loader_config: { accountID, trustKey, agentID, licenseKey, applicationID }
      });
      });
    } catch (e) {
      console.error('[NewRelic] BrowserAgent init failed:', e);
    } finally {
      console.warn = nativeWarn;
      console.error = nativeError;
    }
  }

  /**
   * Reports a JavaScript error to New Relic with optional custom attributes.
   * Use this inside Angular's ErrorHandler or at any caught-error boundary.
   * @param error - The Error object or string to report.
   * @param customAttributes - Optional key-value pairs attached to the error event.
   */
  noticeError(error: Error | string, customAttributes?: Record<string, string | number | boolean>): void {
    if (!this.agent) return;
    try {
      const err = typeof error === 'string' ? new Error(error) : error;
      const enriched: Record<string, string | number | boolean> = {
        timestamp: new Date().toISOString(),
        errorName: err.name,
        errorMessage: err.message,
        errorStack: ((err as any).originalStack ?? err.stack ?? '').slice(0, 4096),
        userAgent: navigator.userAgent,
        pageUrl: window.location.href,
        pagePath: window.location.pathname,
        ...customAttributes
      };
      this.agent.noticeError(err, enriched);
      this.log(err.message || 'Error occurred', enriched, 'error');
    } catch {}
  }

  /**
   * Sends a named custom page-action event to New Relic Insights with optional attributes.
   * Use for tracking feature interactions, button clicks, or business events.
   * @param name - Event name (e.g. 'PayrollProcessed', 'FilterApplied').
   * @param attributes - Optional key-value payload to attach to the event.
   */
  addPageAction(name: string, attributes?: Record<string, string | number | boolean>): void {
    if (!this.agent) return;
    this.agent.addPageAction(name, {
      ...attributes,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent
    });
  }

  /**
   * Sets a custom attribute on all subsequent events for the current session.
   * Useful for attaching tenant ID, domain code, or feature flags.
   * @param key - Attribute name.
   * @param value - Attribute value (string, number, or boolean).
   */
  setCustomAttribute(key: string, value: string | number | boolean): void {
    if (!this.agent) return;
    this.agent.setCustomAttribute(key, value);
  }

  /**
   * Associates all New Relic events for this session with the given user ID.
   * Call after a successful login to tie events to a specific user.
   * @param userId - A stable, non-PII identifier (e.g. employee ID or UUID).
   */
  setUserId(userId: string): void {
    if (!this.agent) return;
    this.agent.setUserId(userId);
  }

  /**
   * Sets the application version string reported to New Relic.
   * Call during app initialization to tag events with the deployed version.
   * @param version - Semantic version string (e.g. '5.12.3').
   */
  setApplicationVersion(version: string): void {
    if (!this.agent) return;
    this.agent.setApplicationVersion(version);
  }

  /**
   * Records a named custom event with arbitrary attributes to New Relic.
   * Prefer addPageAction for UI interactions; use this for structured domain events.
   * @param eventType - The custom event type name.
   * @param attributes - Key-value payload describing the event.
   */
  recordCustomEvent(eventType: string, attributes: Record<string, string | number | boolean>): void {
    if (!this.agent) return;
    this.agent.recordCustomEvent(eventType, attributes);
  }

  /**
   * Sends a log message to New Relic with optional attributes.
   * Falls back to the global window.newrelic.log when the BrowserAgent does not expose log directly.
   * @param message - The log message string.
   * @param attributes - Optional key-value pairs attached to the log entry.
   */
  /**
   * Sets the current route/page name for SPA navigation tracking in New Relic.
   * Use on every NavigationEnd so New Relic groups page views by route instead of URL.
   * @param name - Human-readable page name (e.g. 'Payroll.ProcessSalary').
   */
  setPageViewName(name: string): void {
    if (!this.isEnabled) return;
    const nr: any = (window as any).newrelic;
    if (nr?.setCurrentRouteName) {
      nr.setCurrentRouteName(name);
    }
  }

  /**
   * Records a named metric value as a custom event to New Relic.
   * @param name - Metric name (e.g. 'RouteChangeDuration').
   * @param value - Numeric measurement.
   * @param unit - Optional unit label (e.g. 'ms').
   */
  trackMetric(name: string, value: number, unit?: string): void {
    if (!this.isEnabled) return;
    this.recordCustomEvent('Metric', { name, value, ...(unit ? { unit } : {}) });
  }

  log(
    message: string,
    attributes?: Record<string, string | number | boolean>,
    level: 'debug' | 'error' | 'info' | 'trace' | 'warn' = 'info',
  ): void {
    if (!this.isEnabled) return;
    const options = { level, customAttributes: { ...attributes } };

    // window.newrelic === this.agent (BrowserAgent sets window.newrelic = this).
    // The log method is added by the logging feature which loads asynchronously —
    // retry up to 5 times (50 ms apart) so early calls aren't silently dropped.
    const logFn: Function | undefined =
      (window as any).newrelic?.log ?? (this.agent as any)?.log;

    if (typeof logFn === 'function') {
      try {
        logFn.call((window as any).newrelic ?? this.agent, message, options);
      } catch {}
      return;
    }
  }
}
